import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import './ExportButton.css';

interface ExportButtonProps {
  chartRef: React.RefObject<HTMLDivElement | null>;
  pivotData: any[];
  productId: string;
  timeframe: string;
  showPrice: boolean;
  priceData?: any[];
}

const ExportButton: React.FC<ExportButtonProps> = ({
  chartRef,
  pivotData,
  productId,
  timeframe,
  showPrice,
  priceData
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const generateFilename = (extension: string) => {
    const date = new Date().toISOString().split('T')[0];
    return `pivot-levels-${productId}-${timeframe}-${date}.${extension}`;
  };

  const exportToPNG = async () => {
    if (!chartRef.current) return;
    
    setIsExporting(true);
    setShowMenu(false);
    
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#1a1a2e',
        scale: 2, // Higher resolution
        logging: false,
      });
      
      const link = document.createElement('a');
      link.download = generateFilename('png');
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error exporting to PNG:', error);
      alert('Failed to export chart as PNG');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToCSV = () => {
    setIsExporting(true);
    setShowMenu(false);
    
    try {
      const headers = ['timestamp', 'PP', 'R1', 'R2', 'R3', 'S1', 'S2', 'S3'];
      if (showPrice && priceData) {
        headers.push('price');
      }
      
      const rows = pivotData.map(point => {
        const row = [
          new Date(point.timestamp).toISOString(),
          point.pp,
          point.r1,
          point.r2,
          point.r3,
          point.s1,
          point.s2,
          point.s3
        ];
        
        if (showPrice && priceData) {
          // Find matching price data point
          const pricePoint = priceData.find(p => 
            Math.abs(new Date(p.timestamp).getTime() - point.timestamp) < 3600000
          );
          row.push(pricePoint?.close || '');
        }
        
        return row.join(',');
      });
      
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = generateFilename('csv');
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('Failed to export chart data as CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToJSON = () => {
    setIsExporting(true);
    setShowMenu(false);
    
    try {
      const exportData = {
        metadata: {
          product: productId,
          timeframe: timeframe,
          exportDate: new Date().toISOString(),
          dataPoints: pivotData.length,
          priceOverlayEnabled: showPrice
        },
        pivotLevels: pivotData.map(point => ({
          timestamp: new Date(point.timestamp).toISOString(),
          pivotPoint: point.pp,
          resistance: {
            r1: point.r1,
            r2: point.r2,
            r3: point.r3
          },
          support: {
            s1: point.s1,
            s2: point.s2,
            s3: point.s3
          }
        })),
        priceData: showPrice && priceData ? priceData.map(p => ({
          timestamp: new Date(p.timestamp).toISOString(),
          close: p.close
        })) : null
      };
      
      const json = JSON.stringify(exportData, null, 2);
      const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = generateFilename('json');
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error exporting to JSON:', error);
      alert('Failed to export chart data as JSON');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="export-button-container" ref={menuRef}>
      <button
        className="export-button"
        onClick={() => setShowMenu(!showMenu)}
        disabled={isExporting}
        title="Export chart data"
      >
        {isExporting ? (
          <>
            <span className="export-spinner"></span>
            Exporting...
          </>
        ) : (
          <>
            <span className="export-icon">⬇</span>
            Export
          </>
        )}
      </button>
      
      {showMenu && !isExporting && (
        <div className="export-menu">
          <button
            className="export-menu-item"
            onClick={exportToPNG}
          >
            <span className="export-menu-icon">🖼️</span>
            PNG Image
            <span className="export-menu-desc">Chart screenshot</span>
          </button>
          <button
            className="export-menu-item"
            onClick={exportToCSV}
          >
            <span className="export-menu-icon">📊</span>
            CSV Data
            <span className="export-menu-desc">Spreadsheet format</span>
          </button>
          <button
            className="export-menu-item"
            onClick={exportToJSON}
          >
            <span className="export-menu-icon">📄</span>
            JSON Data
            <span className="export-menu-desc">Full metadata</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportButton;
